import { type VehicleType } from "../../../model/VehicleType";

/** Minimal map selection when a realtime vehicle is clicked (trip + route color). */
export interface RealtimeTransitPathSelection {

    /** Trip identifier used to look up schedule/shape/delay in the cache. */
    "tripId": string;

    /** Route color as CSS rgb values. */
    "color": {
        "r": number;
        "g": number;
        "b": number;
    };
}

/** Realtime vehicle info shown in the Dynamic Island vehicle chrome. */
export interface PopupContent {

    /** Type of vehicle */
    "type": VehicleType;

    /** Vehicle label/identifier (line number) */
    "label": string;

    /** Trip destination sign */
    "trip_headsign"?: string;

    /** France-specific route type code from realtime schedule (e.g. TER, OGO). */
    "route_type_france"?: string | null;

    /** Original trip headsign before display normalization; shown on island subline when distinct. */
    "trip_headsign_original"?: string | null;

    /** Next stop name */
    "stop_name": string;

    /** Arrival time */
    "arrival_time": string;

    /** Delay information */
    "delay": string;

    /**
     * Signed delay in seconds at the next stop (positive = late).
     * Present only when realtime delay data exists.
     */
    "delaySec"?: number;

    /** Vehicle color (RGB) */
    "color"?: {
        "r": number;
        "g": number;
        "b": number;
    };
}

/**
 * Map-domain snapshot of the currently selected vehicle (content + live position).
 * Emitted by MapComponent; consumed by the Dynamic Island orchestrator via a mapper.
 */
export interface VehicleSelectionInfo {
    "content": PopupContent;
    "lngLat":  [number, number];

    /** Stable trip id for animation keys; ignores RT field churn. */
    "tripId": string;
}

/**
 * Returns whether coordinates are finite and not the layer's zero placeholder.
 *
 * @param lngLat Longitude/latitude pair from the vehicle layer.
 * @returns True when the pair can be used for map focus or island payloads.
 */
export function isValidVehicleSelectionLngLat( lngLat: [number, number] ): boolean {
    const [ lon, lat ] = lngLat;

    return Number.isFinite( lon ) &&
      Number.isFinite( lat ) &&
      !( lon === 0 && lat === 0 );
}
